import * as React from 'react'
import Media from 'react-media'
import loadable from '@loadable/component'

const FavouritesOutline = loadable(
  () => import('@thg-commerce/gravity-icons/src/components/FavouritesOutline'),
  { ssr: true, fallback: <div style={{ width: 24, height: 24 }} /> },
)

import { withTheme, mq, BreakpointKey } from '@thg-commerce/gravity-theme'
import { isMobileDevice } from '@thg-commerce/gravity-system'

import {
  Logo,
  AccountDropdownIcon,
  Basket,
  FlyoutPresenter,
} from './components'
import {
  HeaderWrapper,
  Container,
  SearchWrapper,
  NavigationInnerWrapper,
  AccountWrapper,
  BasketWrapper,
  WishlistWrapper,
  FlyoutWrapper,
  SubmenuWrapper,
  WishlistLink,
} from './styles'
import { CombinedThemeInterface } from '..'
import { Navigation } from '../Header/Navigation'
import { HeaderProps } from '../Header/Header'
import { HeaderSearch } from '../Header/HeaderSearch'
import { DropdownOptions } from '../Header/types'

import {
  HeaderComponent,
  HeaderThemeInterface,
  FlyoutDirection,
} from '../Header/theme'
import { Submenu } from '../Header/Submenu'
import { AccountDropdown } from '../Header/AccountDropdown'

const slotComponentMap: {
  [component in HeaderComponent]: (
    props: HeaderProps,
    isDesktop: boolean,
    headerTheme: HeaderThemeInterface,
    breakpoint: BreakpointKey,
  ) => React.ReactNode
} = {
  logo: (props) => {
    return (
      <div style={{ gridArea: HeaderComponent.LOGO, width: '100%' }}>
        <Logo
          logoAriaLabel={props.i18nText.logoAriaLabel}
          logoLink={props.logoLink}
        />
      </div>
    )
  },
  navigation: (props, isDesktop) => {
    return (
      <div
        style={{
          gridArea: HeaderComponent.NAVIGATION,
          width: '100%',
          display: isDesktop ? 'block' : 'none',
        }}
      >
        <NavigationInnerWrapper>
          <Navigation
            truncateNavigationItems={true}
            brandsData={props.brands.brandsData}
            routes={{
              viewAllBrandsLink: props.brands.viewAllBrandsLink,
              listExtension: props.routes.listExtension,
            }}
            navigationTree={props.navigationTree}
            hasDynamicHeaderDropdown={props.hasDynamicHeaderDropdown}
            hasWaterfallMenu={props.hasWaterfallMenu}
            i18nText={{
              categoryHomeText: props.flyoutI18nText.linkWithChildrenLabel,
              viewAllBrandsText: props.brands.i18nText.viewAllBrands,
              navAriaLabel: props.brands.i18nText.navAriaLabel,
            }}
          />
        </NavigationInnerWrapper>
      </div>
    )
  },
  search: (props, isDesktop, headerTheme) => {
    return (
      <SearchWrapper mobileSearch={headerTheme.mobileSearch}>
        <HeaderSearch
          currency={props.headerSearch.currency}
          shippingDestination={props.headerSearch.shippingDestination}
          isMobile={!isDesktop}
          headerSearchI18nText={{
            placeholder: props.i18nText.searchPlaceholder,
            searchButtonAriaLabel: props.i18nText.searchButtonAriaLabel,
            clearAriaLabel: props.i18nText.clearAriaLabel,
          }}
          InstantSearchInjector={props.headerSearch.InstantSearchInjector}
          searchI18nText={props.headerSearch.searchI18nText}
          onSubmit={props.headerSearch.onSubmit}
          autocompleteLink={props.headerSearch.autocompleteLink}
          renderAnnouncer={props.headerSearch.renderAnnouncer}
          triggerAriaLabels={{
            close: props.headerDropdownI18nText.closeButtonAriaLabel,
            search: props.i18nText.searchButtonAriaLabel,
          }}
          isAmp={props.isAmp}
          mobileIcon={headerTheme.search.mobileIcon}
          onFocus={props.headerSearch.onFocus}
          mobileSearch={isDesktop && headerTheme.mobileSearch}
          slimHeader={true}
        />
      </SearchWrapper>
    )
  },
  account: (props, isDesktop) => {
    return (
      <AccountWrapper>
        <AccountDropdown {...props} isDesktop={isDesktop} newHeaderEnabled />
      </AccountWrapper>
    )
  },
  basket: (props, isDesktop, headerTheme) => {
    return (
      <BasketWrapper>
        <Basket
          isDesktop={isDesktop}
          route={props.routes.basket}
          dropdownWidth={headerTheme.dropdowns.basket.width}
          {...props}
        />
      </BasketWrapper>
    )
  },
  submenu: (props, isDesktop, headerTheme) => {
    return (
      isDesktop && (
        <SubmenuWrapper>
          <Submenu
            isDesktop={isDesktop}
            {...props.submenu}
            accountDropdownProps={{
              isMobile: !isDesktop,
              trigger: {
                icon: (
                  <AccountDropdownIcon
                    accountIcon={headerTheme.dropdowns.account.icon.svgPath}
                  />
                ),
                text: props.i18nText.accountLabel,
                triggerHref: props.routes.accountHome,
              },
              dropdownWidth: headerTheme.dropdowns.account.width,
              content: props.accountNav,
              i18nAriaLabels: {
                close: props.headerDropdownI18nText.closeButtonAriaLabel,
                trigger: props.i18nText.accountButtonAriaLabel,
              },
              disableFocusLogic: false,
              notification: props.wishlistNotification,
              dropdownType: DropdownOptions.ACCOUNT,
            }}
          />
        </SubmenuWrapper>
      )
    )
  },
  flyout: (props, isDesktop, headerTheme, breakpoint) => {
    return (
      !isDesktop && (
        <FlyoutWrapper>
          <FlyoutPresenter
            data-testid="flyout"
            isDesktop={isDesktop}
            i18nText={props.i18nText}
            headerDropdownI18nText={props.headerDropdownI18nText}
            navigationTree={props.navigationTree}
            flyoutI18nText={props.flyoutI18nText}
            routes={props.routes}
            accountNav={props.accountNav}
            submenu={props.submenu}
            headerSlots={props.headerSlots}
            responsiveFlyoutMenuIcons={props.responsiveFlyoutMenuIcons}
            slots={headerTheme.slots}
            flyoutIcon={headerTheme.dropdowns.account.icon.svgPath}
            dropdownWidth={headerTheme.dropdowns.account.width}
            burgerIconColor={headerTheme.navigation.burgerIconColor}
            flyoutMenuIcon={headerTheme.flyout.icon}
            flyoutDirection={
              headerTheme.flyout.direction[breakpoint] as FlyoutDirection
            }
            accountDropdownProps={{
              isMobile: !isDesktop,
              trigger: {
                icon: (
                  <AccountDropdownIcon
                    accountIcon={headerTheme.dropdowns.account.icon.svgPath}
                  />
                ),
                text: props.i18nText.accountLabel,
                triggerHref: props.routes.accountHome,
              },
              dropdownWidth: headerTheme.dropdowns.account.width,
              content: props.accountNav,
              i18nAriaLabels: {
                close: props.headerDropdownI18nText.closeButtonAriaLabel,
                trigger: props.i18nText.accountButtonAriaLabel,
              },
              disableFocusLogic: false,
              notification: props.wishlistNotification,
              dropdownType: DropdownOptions.ACCOUNT,
            }}
          />
        </FlyoutWrapper>
      )
    )
  },
  wishlist: (props, isDesktop, headerTheme) => {
    return (
      isDesktop && (
        <WishlistWrapper>
          <WishlistLink
            href={props.routes.wishlist}
            aria-label={props.i18nText.wishlistAriaLabel}
          >
            {headerTheme.wishlist.icon.svgPath !== '' ? (
              <svg
                data-name="Layer 1"
                xmlns="http://www.w3.org/2000/svg"
                viewBox={headerTheme.wishlist.icon.viewBox}
              >
                <path
                  d={headerTheme.wishlist.icon.svgPath}
                  fill-rule="evenodd"
                ></path>
                <title>{props.i18nText.wishlistAriaLabel}</title>
              </svg>
            ) : (
              <FavouritesOutline />
            )}
          </WishlistLink>
        </WishlistWrapper>
      )
    )
  },
}

export const NewHeader = withTheme(
  (props: HeaderProps & { theme: CombinedThemeInterface }) => {
    const enabledComponents = props.theme.patterns.header.enabledComponents

    return (
      <HeaderWrapper sticky={props.sticky}>
        <Container data-testid="new-header">
          <Media
            query={mq(props.theme.breakpointUtils.map, 'lg', true)}
            defaultMatches={!isMobileDevice(props.userAgent)}
          >
            {(isDesktop) =>
              enabledComponents.lg.map((component) => {
                return slotComponentMap[component](
                  props,
                  isDesktop,
                  props.theme.patterns.header,
                  'lg',
                )
              })
            }
          </Media>
          <Media
            queries={{
              md: `${mq(
                props.theme.breakpointUtils.map,
                'md',
                true,
              )} and (max-width: calc(${
                props.theme.breakpointUtils.map.lg
              }px - 1px))`,
            }}
            defaultMatches={{ md: isMobileDevice(props.userAgent) }}
            render={() => {
              return enabledComponents.md.map((component) => {
                return slotComponentMap[component](
                  props,
                  false,
                  props.theme.patterns.header,
                  'md',
                )
              })
            }}
          />
          <Media
            queries={{
              sm: `${mq(
                props.theme.breakpointUtils.map,
                'sm',
                true,
              )} and (max-width: calc(${
                props.theme.breakpointUtils.map.md
              }px - 1px))`,
            }}
            defaultMatches={{ sm: isMobileDevice(props.userAgent) }}
            render={() => {
              return enabledComponents.sm.map((component) => {
                return slotComponentMap[component](
                  props,
                  false,
                  props.theme.patterns.header,
                  'sm',
                )
              })
            }}
          />
          <Media
            queries={{
              xs: `${mq(
                props.theme.breakpointUtils.map,
                'xs',
                true,
              )} and (max-width: calc(${
                props.theme.breakpointUtils.map.sm
              }px - 1px))`,
            }}
            defaultMatches={{ xs: isMobileDevice(props.userAgent) }}
            render={() => {
              return enabledComponents.xs.map((component) => {
                return slotComponentMap[component](
                  props,
                  false,
                  props.theme.patterns.header,
                  'xs',
                )
              })
            }}
          />
        </Container>
      </HeaderWrapper>
    )
  },
)
